import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import cn from 'clsx';
import CreatedUpdatedInfo from '@components-clean/core/created-updated-info';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { MultiSelectController } from '@components-clean/core/multi-select';
import { TextBoxController } from '@components-clean/core/text-box';
import { useOnValueChange, useUnsavedChangesConfirmation, useUnsavedChangesTracking } from '@hooks-clean/index';
import { EditReviewDto, ExistingReview } from '@interfaces-clean/reviews.interface';
import { StudyMemberDto } from '@interfaces-clean/study-member.interface';
import styles from '@pages-clean/subject-monitoring/components/monitoring-toolkit/_shared/review-plan-details/index.module.scss';
import { isValidReviewTitle } from '@utils-clean/validation';
import { useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { ShowError, ShowSuccess } from '@utils/notification';
import { SubjectMonitoringReviewAPI } from '@apis/bff-api/subject-monitoring-review';

type ReviewersMultiSelectListItem = {
    id: string;
    name: string;
    active: boolean;
    groupedBy: 'Active' | 'Inactive';
    disabled: boolean;
};

type ReviewPlanForm = {
    title: string;
    reviewers: ReviewersMultiSelectListItem[];
};

const WIDTH = 600;
const HEIGHT = 300;
export const COMPONENT_NAME = 'active-review-edit-review-plan-details-dialog';

type Props = {
    studyRef: string;
    review: EditReviewDto;
    smStudyMembers: StudyMemberDto[];
    closeFn: () => void;
    saveFn: () => void;
    existingReviews: ExistingReview[];
};

const EditReviewDetails = ({ studyRef, review, smStudyMembers, closeFn, saveFn, existingReviews }: Props) => {
    const { t } = useTranslation();
    const { t: tReviewPlan } = useTranslation('sm', {
        keyPrefix: 'monitoringToolkit.activeReviewTab.tabs.reviewPlan.reviewPlan',
    });

    const { trigger: updateReview, isMutating: isUpdatingReview } = SubjectMonitoringReviewAPI.useUpdateReview(
        studyRef,
        review.recordId!,
    );

    const smStudyMembersListOptions = useMemo<ReviewersMultiSelectListItem[]>(() => {
        const reviewersOnLoadIdSet = new Set(review.reviewers?.map((x) => x.ref).filter((x) => x != null));

        return smStudyMembers
            .filter((x) => typeof x.ref === 'string' && typeof x.name === 'string' && typeof x.acitve === 'boolean')
            .map((x) => {
                return {
                    id: x.ref!,
                    name: x.name!,
                    active: x.acitve!,
                    groupedBy: x.acitve ? 'Active' : 'Inactive',
                    disabled: reviewersOnLoadIdSet.has(x.ref!) ? false : !x.acitve,
                };
            });
    }, [review, smStudyMembers]);

    const defaultValues = useMemo<ReviewPlanForm>(() => {
        const smStudyMembersByIdMap = new Map(smStudyMembersListOptions.map((x) => [x.id, x]));

        const reviewers = (review?.reviewers ?? [])
            .map((x) => smStudyMembersByIdMap.get(x.ref!))
            .filter((x) => x != null);

        return {
            title: review?.title ?? '',
            reviewers,
        };
    }, [review, smStudyMembersListOptions]);

    const {
        control,
        handleSubmit,
        reset,
        formState: { isDirty, isValid, isSubmitting },
    } = useForm<ReviewPlanForm>({
        defaultValues,
        mode: 'all',
    });

    useOnValueChange(defaultValues, () => {
        reset(defaultValues, { keepDirty: false });
    });

    const createdByText =
        review.createdByName != null && review.createdDate != null
            ? `${review.createdByName} ${stringDateWithoutTimeFromUtc(review.createdDate, 'DD-MMM-YYYY')}`
            : null;

    const updatedByText =
        review.updatedByName != null && review.updatedDate != null
            ? `${review.updatedByName} ${stringDateWithoutTimeFromUtc(review.updatedDate, 'DD-MMM-YYYY')}`
            : null;

    useUnsavedChangesTracking({
        area: 'subject-monitoring-toolkit',
        componentName: COMPONENT_NAME,
        hasChanges: isDirty,
    });

    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'subject-monitoring-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAME,
    });

    const canSave = isDirty && isValid && !isSubmitting;

    const onClose = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) return;
        reset();
        closeFn();
    };

    const onSave = async () => {
        await handleSubmit(async (saveData) => {
            try {
                await updateReview({
                    type: review.type!,
                    title: saveData.title,
                    datasetId: review.datasetId!,
                    reviewed: review.reviewed!,
                    reviewStartDate: review.reviewStartDate,
                    reviewEndDate: review.reviewEndDate,
                    reviewSummary: review.reviewSummary,
                    studyMemberRefs: saveData.reviewers.map((x) => x.id),
                });
                ShowSuccess(t('actionsSuccessfulToast'));
                saveFn();
            } catch {
                ShowError(t('errorMessage'));
                closeFn();
            }
        })();
    };

    const createdUpdatedInfo =
        createdByText != null && updatedByText != null ? (
            <CreatedUpdatedInfo created={createdByText} updated={updatedByText} width={WIDTH} />
        ) : undefined;

    return (
        <DraggableDialog
            dataTestId="edit-review-plan-details-dialog"
            title={tReviewPlan('editReviewDetails')}
            primaryBtnLabel={t('save')}
            primaryBtnDisabled={!canSave || isUpdatingReview}
            secondaryBtnLabel={t('cancel')}
            secondaryBtnDisabled={isUpdatingReview}
            onClose={onClose}
            onSubmit={onSave}
            initialWidth={WIDTH}
            initialHeight={HEIGHT}
            createdUpdatedInfo={createdUpdatedInfo}
            bodyPadding="none"
        >
            <div className={cn('k-w-full k-h-full k-d-flex-col')}>
                <div className={cn(styles.reviewPlanSection)}>
                    <div className={cn(styles.gridCellInner, styles.reviewPlanSectionInner)}>
                        <div className={cn(styles.reviewPlanCardHeader)}>
                            <div className={styles.reviewPlanCardHeaderSection}>
                                <span className={styles.reviewPlanCardHeaderTitle}>{tReviewPlan('type')}</span>
                                <span className={styles.reviewPlanCardHeaderValue}>{review.type}</span>
                            </div>
                            <div className={styles.reviewPlanCardHeaderSection}>
                                <span className={styles.reviewPlanCardHeaderTitle}>{tReviewPlan('dataset')}</span>
                                <span className={styles.reviewPlanCardHeaderValue}>{review.datasetDate}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={cn('k-pt-md k-px-md')}>
                    <TextBoxController
                        data-testid="review-plan-title"
                        control={control}
                        name="title"
                        label={tReviewPlan('fields.title')}
                        maxLength={30}
                        required
                        validateRules={{
                            noInvalidCharacters: (value) => {
                                if (value && !isValidReviewTitle(value as string)) {
                                    return tReviewPlan('invalidChars');
                                }
                                return true;
                            },
                            checkExistingReviews: (value) => {
                                if (value) {
                                    const trimmed = (value as string).trim().toLowerCase();
                                    if (
                                        existingReviews.some(
                                            (rev) =>
                                                rev?.id !== review.id &&
                                                rev?.title?.toLowerCase() === trimmed &&
                                                rev.datasetId === review.datasetId &&
                                                rev.type === review.type,
                                        )
                                    ) {
                                        return tReviewPlan('reviewAlreadyExists');
                                    }
                                }
                                return true;
                            },
                        }}
                    />

                    <MultiSelectController
                        data-testid="review-plan-reviewers"
                        isEditing
                        control={control}
                        name="reviewers"
                        label={tReviewPlan('fields.reviewers')}
                        data={smStudyMembersListOptions}
                        dataItemKey="id"
                        textField="name"
                        activeField="active"
                        allowCustom={false}
                        maxLines={2}
                    />
                </div>
            </div>
        </DraggableDialog>
    );
};

export default EditReviewDetails;
